import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { brand } from '../branding';
import { isPromptSuppressedPath } from '../utils/promptSuppression';
import { isStandalone, isIosSafari } from '../utils/platform';

// Custom PWA install prompt. Replaces the browser's default mini-infobar
// with our own UI so we control timing, copy, and dismiss behaviour.
//
// Two branches:
//   - Chromium (desktop + Android): `beforeinstallprompt` fires. We stash
//     the event and call .prompt() from a real click handler.
//   - iOS Safari: that event never fires. We show an instructional card
//     telling the user to tap Share → Add to Home Screen.
//
// In-app browsers (iOS Chrome/Firefox/Facebook) can't install at all, so
// we suppress the prompt there entirely.

// LAYERING: this sheet sits at z-40, BELOW the z-50 modal layer that every
// dialog in the app uses (Modal.tsx, AcknowledgeModal, ConfirmDialog, ...).
// A modal is the user's active task; an install nudge is advisory, and the
// advisory layer must never sit above it. At equal z-index DOM order decides —
// and these prompts mount after <App /> in index.tsx, so on a phone the sheet
// would fully cover a dialog's primary button (e.g. policy signing's Sign).
const DISMISS_STORAGE_KEY = 'sproutly.installPrompt.dismissedAt';
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

const wasRecentlyDismissed = (): boolean => {
  try {
    const raw = window.localStorage.getItem(DISMISS_STORAGE_KEY);
    if (!raw) return false;
    const ts = Number.parseInt(raw, 10);
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < DISMISS_COOLDOWN_MS;
  } catch {
    return false;
  }
};

const recordDismiss = () => {
  try {
    window.localStorage.setItem(DISMISS_STORAGE_KEY, String(Date.now()));
  } catch {
    /* private mode / storage disabled — fine, we'll just ask again next load */
  }
};

const InstallPrompt: React.FC = () => {
  const location = useLocation();
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosHint, setShowIosHint] = useState(false);
  // Soft-hide once the user starts scrolling: the sheet is a bottom-anchored
  // overlay and can sit over content (incident cards, quick actions). Scrolling
  // signals the user is reading, not installing — so step out of the way. This
  // does NOT record a dismiss, so the nudge returns on the next page load; only
  // an explicit dismiss starts the 7-day cooldown.
  const [hiddenByScroll, setHiddenByScroll] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > 120) setHiddenByScroll(true);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed()) return undefined;

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setDeferred(null);
      setShowIosHint(false);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    if (isIosSafari()) setShowIosHint(true);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
  };

  const handleDismiss = () => {
    recordDismiss();
    setDeferred(null);
    setShowIosHint(false);
  };

  // Never cover a safety-critical action bar. The live fire drill and its
  // wrap-up render a `sticky bottom-0` footer holding the primary action,
  // which this fixed bottom sheet would sit on top of and intercept taps on
  // "Complete drill". An install nudge is never more urgent than finishing a
  // statutory evacuation record — defer it.
  if (isPromptSuppressedPath(location.pathname)) return null;

  // Stepped aside while the user scrolls through content — reappears next load.
  if (hiddenByScroll) return null;

  if (deferred) {
    return (
      <>
        {/* Mobile-only dimmed backdrop so the sheet reads as the active task,
            not just another piece of chrome. Tap-to-dismiss feels right here. */}
        <div
          data-testid="install-prompt-backdrop"
          className="fixed inset-0 bg-black/40 z-30 sm:hidden"
          onClick={handleDismiss}
          aria-hidden="true"
        />
        <div
          data-testid="install-prompt"
          role="dialog"
          aria-label={`Install ${brand.name}`}
          aria-modal="true"
          className="fixed bottom-0 left-0 right-0 z-40 rounded-t-2xl bg-white shadow-2xl p-6 pb-8 sm:bottom-6 sm:right-6 sm:left-auto sm:max-w-md sm:rounded-2xl sm:p-6 sm:pb-6 sm:border sm:border-gray-200 animate-slide-up"
        >
          <button
            data-testid="install-prompt-close"
            onClick={handleDismiss}
            aria-label="Dismiss"
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 p-2"
          >
            <svg
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <div className="flex flex-col items-center text-center sm:flex-row sm:items-start sm:text-left sm:gap-4">
            <img
              src="/logo192.png"
              alt=""
              className="h-20 w-20 rounded-2xl shadow-md mb-4 sm:mb-0 sm:h-16 sm:w-16 sm:flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <p className="text-xl font-bold text-gray-900 sm:text-lg">Install {brand.name}</p>
              <p className="mt-2 text-sm text-gray-600 sm:mt-1">
                Add {brand.name} to your home screen for one-tap access and a full-screen
                experience.
              </p>
              <div className="mt-5 flex flex-col gap-2 sm:mt-4 sm:flex-row sm:gap-3">
                <button
                  data-testid="install-prompt-accept"
                  onClick={handleInstall}
                  className="w-full rounded-lg bg-primary-600 px-4 py-3 text-base font-semibold text-white shadow-sm hover:bg-primary-700 sm:w-auto sm:py-2 sm:text-sm"
                >
                  Install app
                </button>
                <button
                  data-testid="install-prompt-dismiss"
                  onClick={handleDismiss}
                  className="w-full rounded-lg px-4 py-3 text-base font-medium text-gray-600 hover:bg-gray-100 sm:w-auto sm:py-2 sm:text-sm"
                >
                  Not now
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (showIosHint) {
    return (
      <>
        <div
          data-testid="install-prompt-backdrop"
          className="fixed inset-0 bg-black/40 z-30 sm:hidden"
          onClick={handleDismiss}
          aria-hidden="true"
        />
        <div
          data-testid="install-prompt-ios"
          role="dialog"
          aria-label={`Install ${brand.name} on iOS`}
          aria-modal="true"
          className="fixed bottom-0 left-0 right-0 z-40 rounded-t-2xl bg-white shadow-2xl p-6 pb-8 sm:bottom-6 sm:right-6 sm:left-auto sm:max-w-md sm:rounded-2xl sm:p-6 sm:pb-6 sm:border sm:border-gray-200 animate-slide-up"
        >
          <button
            data-testid="install-prompt-ios-close"
            onClick={handleDismiss}
            aria-label="Dismiss"
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 p-2"
          >
            <svg
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <div className="flex flex-col items-center text-center sm:flex-row sm:items-start sm:text-left sm:gap-4">
            <img
              src="/logo192.png"
              alt=""
              className="h-20 w-20 rounded-2xl shadow-md mb-4 sm:mb-0 sm:h-16 sm:w-16 sm:flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <p className="text-xl font-bold text-gray-900 sm:text-lg">Install {brand.name}</p>
              <p className="mt-2 text-sm text-gray-600 sm:mt-1">
                Add {brand.name} to your home screen for one-tap access. In Safari, tap the{' '}
                <span aria-label="Share" className="inline-flex items-center align-middle mx-0.5">
                  <svg
                    className="h-5 w-5 text-primary-600"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M12 3v12m0-12l-4 4m4-4l4 4M5 13v5a2 2 0 002 2h10a2 2 0 002-2v-5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>{' '}
                Share button, then choose{' '}
                <span className="font-semibold text-gray-900">Add to Home Screen</span>.
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return null;
};

export default InstallPrompt;
